import ApplyV2Content from '@core/components/ApplyDialogV2/ApplyV2Content'
import type { ApplyV2SettingsSectionName, NavItemsState } from '@core/app/types/applyV2Type'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import React, { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip, useMediaQuery } from '@chakra-ui/react'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import CloseApplyModal from '@core/components/ApplyDialogV2/CloseApplyModal'
import clsx from 'clsx'
import ApplyNavBtn from '@core/components/ApplyDialogV2/ApplyNavBtn'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { useTranslation } from 'react-i18next'
import './ApplyDialogV2.scss'

const baseClass = 'ApplyDialogV2'

interface ApplyDialogV2Props {
    className?: string
    children?: ReactNode
}

function ApplyDialogV2({ className = '', children }: ApplyDialogV2Props) {
    const {
        onOpenApply,
        isExpanded,
        isOpen,
        onCloseApply,
        onResizeApply,
        settingsData,
        settingsRefetch,
        settingsError,
        storageData,
        isSettingsFetching,
        isPostFetching,
        postData,
        onSelectStep,
        navItems,
        isFinalStep,
        existingSteps,
    } = useApplyV2Context()
    const { t } = useTranslation()
    const nameRef = useRef<HTMLSpanElement>(null)
    const [isEllipsed, setIsEllipsed] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isDesktop] = useMediaQuery('(min-width: 800px)')
    const [isMobile] = useMediaQuery('(max-width: 1229px)')

    const sliderRef = useRef<HTMLDivElement>(null)
    // const [activeIndex, setActiveIndex] = useState(0)
    const [activeSlide, setActiveSlide] = useState<ApplyV2SettingsSectionName | null>(null)
    const { getTitle } = useApplyV2NavTranslations()

    const scrollToSlide = (slide: ApplyV2SettingsSectionName | null) => {
        if (!sliderRef.current) return
        const slideWidth = sliderRef.current.clientWidth
        const index = navItems.findIndex((item) => item.name === slide)
        sliderRef.current.scrollTo({
            left: index * slideWidth,
            // @ts-ignore
            behavior: 'instant',
        })
        setActiveSlide(slide)
    }

    // useEffect(() => {
    //     if (isOpen && isExpanded) {
    //         scrollToSlide(storageData?.currentStep ?? navItems[0].name)
    //     }
    // }, [storageData, navItems, isOpen, isExpanded])

    useEffect(() => {
        if (!isOpen || !isExpanded || !sliderRef.current || !navItems[0]) return

        const observer = new ResizeObserver(() => {
            scrollToSlide(storageData?.currentStep ?? navItems[0]?.name)
        })

        observer.observe(sliderRef.current)

        return () => {
            observer.disconnect()
        }
    }, [storageData, navItems, isOpen, isExpanded])

    useEffect(() => {
        const handleResize = () => {
            scrollToSlide(activeSlide)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [activeSlide])

    const isLoading = isSettingsFetching || isPostFetching

    useEffect(() => {
        const el = nameRef.current
        if (el) {
            setIsEllipsed(el.scrollWidth > el.clientWidth)
        }
    }, [isOpen, isExpanded, postData])

    // useEffect(() => {
    //     const handleResize = () => {
    //         const el = nameRef.current
    //         if (el) {
    //             setIsEllipsed(el.scrollWidth > el.clientWidth)
    //         }
    //     }
    //
    //     window.addEventListener('resize', handleResize)
    //     handleResize()
    //
    //     return () => window.removeEventListener('resize', handleResize)
    // }, [])

    useEffect(() => {
        if (isOpen && isExpanded) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }

        return () => {
            document.body.style.overflow = ''
        }
    }, [isOpen, isExpanded])

    if (!isOpen) return null

    const currentItemName = storageData?.currentStep
    const currentItemIdx = navItems.findIndex((item) => item.name === currentItemName)

    const existingStepIndices = existingSteps
        .map((step) => navItems.findIndex((item) => item.name === step))
        .filter((index) => index !== -1)

    let maxExistingStepIndex = existingStepIndices.length > 0 ? Math.max(...existingStepIndices) : -1

    if (currentItemIdx > maxExistingStepIndex) {
        maxExistingStepIndex = currentItemIdx
    }

    const navItemsWithDisabled = navItems.map((item, index) => {
        if (index <= maxExistingStepIndex) {
            return item
        }

        return {
            ...item,
            //todo add enabled - for dev
            state: 'disabled' as NavItemsState,
        }
    })

    // console.log(process.env.NODE_ENV)

    return (
        <div
            onClick={(e) => {
                if (!isExpanded) {
                    onResizeApply()
                }
            }}
            className={clsx(baseClass, {
                [`${baseClass}__expanded`]: isExpanded,
                [`${baseClass}__opened`]: isOpen,
                [className]: !!className,
            })}
        >
            <CloseApplyModal isModalOpen={isModalOpen} onCloseApply={onCloseApply} setIsModalOpen={setIsModalOpen} />
            <div
                className={clsx(`${baseClass}__header`, {
                    [`${baseClass}__header--opened`]: isOpen,
                    [`${baseClass}__header--expanded`]: isExpanded,
                })}
            >
                <ApplySkeleton isLoading={false} style={{ width: '44px', height: '44px' }}>
                    <Tooltip
                        hasArrow
                        isDisabled={isLoading || isMobile}
                        openDelay={500}
                        label={isExpanded ? 'Collapse' : 'Expand'}
                        placement={isExpanded ? 'top-start' : 'right-start'}
                        padding={'8px 16px'}
                        borderRadius={'4px'}
                        backgroundColor={'#19191A'}
                        fontWeight={600}
                        fontSize={'12px'}
                    >
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                onResizeApply()
                            }}
                            className={clsx(
                                `${baseClass}__header__expand-btn`,
                                `${baseClass}__header__expand-btn-expand`,
                                {
                                    [`${baseClass}__header__expand-btn-expand--isOpened`]: isOpen,
                                    [`${baseClass}__header__expand-btn-expand--isExpanded`]: isExpanded,
                                }
                            )}
                        >
                            {isExpanded ? (
                                <i className="fa-regular fa-chevron-down"></i>
                            ) : (
                                <span className={`${baseClass}__header__expand-btn-expand--mobile`}>
                                    <i className="fa-regular fa-chevron-up"></i>
                                    <span className={`${baseClass}__header__expand-btn-expand--mobile-text`}>
                                        {getTitle('10455', 'Tap to continue your application')}
                                    </span>
                                </span>
                            )}
                        </button>
                    </Tooltip>
                </ApplySkeleton>
                <div
                    className={clsx(`${baseClass}__header__left`, {
                        [`${baseClass}__header__left--opened`]: isOpen,
                        [`${baseClass}__header__left--expanded`]: isExpanded,
                    })}
                >
                    <div className={`${baseClass}__header__title`}>
                        {/*{!isEllipsed && (*/}
                        {/*    <ApplySkeleton isLoading={isPostFetching}>*/}
                        {/*        <span className={`${baseClass}__header__title--application`}>*/}
                        {/*            {t('1589', 'Application')}*/}
                        {/*        </span>*/}
                        {/*    </ApplySkeleton>*/}
                        {/*)}*/}
                        <ApplySkeleton isLoading={isPostFetching} style={{ maxWidth: '240px' }}>
                            <span className={`${baseClass}__header__title--name`}>
                                <Tooltip
                                    hasArrow
                                    isDisabled={isLoading}
                                    openDelay={500}
                                    label={''}
                                    // label={'We are searching for an exceptionally skilled Marketing Coordinator'}
                                    // placement={'right'}
                                    padding={'8px 16px'}
                                    borderRadius={'4px'}
                                    backgroundColor={'#19191A'}
                                    fontWeight={600}
                                    fontSize={'12px'}
                                >
                                    <span className={`${baseClass}__header__title--name-value`} ref={nameRef}>
                                        {storageData?.title}
                                    </span>
                                </Tooltip>
                            </span>
                        </ApplySkeleton>
                    </div>
                </div>

                {!isExpanded ? (
                    <ApplySkeleton isLoading={isLoading} style={{ width: '44px', height: '44px' }}>
                        <Tooltip
                            hasArrow
                            isDisabled={isLoading}
                            openDelay={500}
                            label={getTitle('10245', 'Close application')}
                            placement={'left-start'}
                            padding={'8px 16px'}
                            borderRadius={'4px'}
                            backgroundColor={'#19191A'}
                            fontWeight={600}
                            fontSize={'12px'}
                        >
                            <button
                                className={`${baseClass}__header__expand-btn ${baseClass}__header__expand-btn-close`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setIsModalOpen(true)
                                }}
                            >
                                <i className="fa-regular fa-xmark-large"></i>
                            </button>
                        </Tooltip>
                    </ApplySkeleton>
                ) : (
                    <div className={`${baseClass}__header__nav`}>
                        {/*<ApplyNavBtn>Default</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn>Text</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'optional'}>Optional</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'active'}>Active</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'success'}>Success</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'error'}>Error</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'error'} isLoading={true}>*/}
                        {/*    Error*/}
                        {/*</ApplyNavBtn>*/}
                        {/*<div className={`${baseClass}__header__nav-items`}>*/}
                        {isSettingsFetching
                            ? Array.from({ length: 5 }).map((_, i) => (
                                <ApplyNavBtn key={i} isLoading={isSettingsFetching} />
                            ))
                            : navItemsWithDisabled.map((item) => (
                                <ApplyNavBtn
                                    key={item.name}
                                    name={item.name}
                                    tooltipLabel={'Go to step'}
                                    isLoading={isSettingsFetching}
                                    onClick={() => onSelectStep(item.name)}
                                    state={item.name === activeSlide ? 'active' : item.state}
                                    data-nav-item={item.name}
                                >
                                    {item.title}
                                </ApplyNavBtn>
                            ))}
                        {/*</div>*/}
                    </div>
                )}
            </div>
            {/*<div className={clsx(`${baseClass}__content`, { [`${baseClass}__content--expanded`]: isExpanded })}>*/}
            {/*    <div className={`${baseClass}__slider-wrapper`} ref={sliderRef}>*/}
            {/*        {navItems.map((item, index) => (*/}
            {/*            <div key={item.name} className={`${baseClass}__slide`}>*/}
            {/*                <div> Слайд: {item.name}</div>*/}
            {/*            </div>*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*</div>*/}

            <ApplyV2Content
                navItems={navItems}
                // activeIndex={activeIndex}
                currentStep={activeSlide}
                sliderRef={sliderRef}
                isExpanded={isExpanded}
                isLoading={isLoading}
                settingsData={settingsData}
                isFinalStep={isFinalStep}
            />
        </div>
    )
}

export default ApplyDialogV2
